import { EventType } from "app/types/eventType";
import { NavigationMetaType } from "app/types/navigationType";
import {
  CompetitionFilterType,
  CompetitionFilterTypes,
  ResponsiveScreenSizesInt,
  TabTypes,
} from "app/utils/const";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";
import CompetitionsScreen from "../CompetitionsScreen/CompetitionsScreen";
import CompetitionsScreenMobile from "../CompetitionsScreenMobile/CompetitionsScreenMobile";
import { useCompetitionsPolling } from "./hooks/useCompetitionsPolling";
import { usePageVisibility } from "./hooks/usePageVisibility";
import {
  handleCompetitionRoute,
  handleEventRoute,
  handleEventsRoute,
  handleFavouritesRoute,
  handleSportRoute,
} from "./routeHandlers";

type Props = {
  setCompetitions: (options?: { fullList?: boolean }) => void;
  setSingleCompetition: (competitionId: number) => void;
  setNavigationNodeIds: (nodeIds: number[]) => void;
  resetNavigationNodeIds: () => void;
  setFilter: (filter: string) => void;
  updateCompetitionListType: (type: string) => void;
  updateCompetitionListTime: (time: string) => void;
  isUserLoading: boolean;
  fullListLoaded: boolean;
  singleCompetitionOnly: boolean;
  isCompetitionListLoading: boolean;
  isUserLoaded: boolean;
  navigationTree: NavigationMetaType[];
  updateFiltering: (
    navNodeIds: number[],
    selectedSport: NavigationMetaType | null,
    selectedCountry: NavigationMetaType | null,
    selectedCompetition: NavigationMetaType | null
  ) => void;
  loadNavigation: () => void;
  setCompetitionInNavigation: (competitionObject: NavigationMetaType) => void;
  updateCountry: (country: NavigationMetaType) => void;
  updateUnsummarizedMarketsModalEvent: (event: EventType | null) => void;
  resetCompetitions: () => void;
  setBookmarksOnly: (bookmarksOnly: boolean) => void;
  setFutures: (futures: boolean) => void;
  setFavouritesOnly: (favouritesOnly: boolean) => void;
  disableFilter: (disableFilterTab: boolean) => void;
  disableFilterTab: boolean;
  selectedCompetitionType: (typeof TabTypes)[keyof typeof TabTypes];
  isIdle: boolean;
  time: string;
  isPageVisible: boolean;
  favouritesOnly: boolean;
  setSingleCompetitionOnly: (singleCompetitionOnly: boolean) => void;
};
const CompetitionsScreenWrapper: FC<Props> = props => {
  const initialRouteHandled = useRef(false);

  const {
    isUserLoading,
    setCompetitions,
    isCompetitionListLoading,
    fullListLoaded,
    updateFiltering,
    loadNavigation,
    navigationTree,
    updateUnsummarizedMarketsModalEvent,
    disableFilter,
    singleCompetitionOnly,
    isPageVisible,
  } = props;

  const [isMobile, setIsMobile] = useState(
    window.innerWidth <= ResponsiveScreenSizesInt.mobile
  );
  const [prevIsIdle, setPrevIsIdle] = useState(props.isIdle);

  const history = useHistory();
  const location = useLocation();
  const { id } = useParams<{
    id?: string;
  }>();
  const isUserLoaded = useRef(false);

  // useMemo to only re-create the query params if the location.search changes
  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const filter = queryParams.get("filter") as CompetitionFilterType;
  const period = (queryParams.get("period") as "today" | "early") || null;
  const country = queryParams.get("country") as string;

  useEffect(() => {
    // need to load the navigation tree to get sport/country/competition data
    loadNavigation();
    // only load navigation once
    // disabling because only need to load this when component mounts
    // and adding loadNavigation to the dependency array causes unnecessary complications
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Add refs to store previous values
  const prevPath = useRef(location.pathname);
  const prevId = useRef(id);
  const prevFilter = useRef(filter);
  const prevPeriod = useRef(period);
  const prevCountry = useRef(country);
  const prevIsPageVisible = useRef(isPageVisible);

  useEffect(() => {
    if (!navigationTree?.length) {
      return;
    }

    // Check if any routing-relevant values have changed
    const hasChanged =
      prevPath.current !== location.pathname ||
      prevId.current !== id ||
      prevFilter.current !== filter ||
      prevPeriod.current !== period ||
      prevCountry.current !== country ||
      prevIsPageVisible.current !== isPageVisible;
    // If nothing has changed and we've handled the initial route, skip
    if (initialRouteHandled.current && !hasChanged) {
      console.log("No changes in route");
      return;
    }

    // Update refs with current values
    prevPath.current = location.pathname;
    prevId.current = id;
    prevFilter.current = filter;
    prevPeriod.current = period;
    prevCountry.current = country;
    prevIsPageVisible.current = isPageVisible;

    const path = location.pathname;
    // If /bookmarks route, then set bookmarksOnly to true
    // Otherwise set it to false for all other routes
    const isBookmarksRoute = path.startsWith("/bookmarks/");
    props.setBookmarksOnly(isBookmarksRoute);
    // Same for futures
    const isFuturesRoute = path.startsWith("/futures/");
    props.setFutures(isFuturesRoute);
    const isFavouritesRoute = path.startsWith("/favourites/");
    props.setFavouritesOnly(isFavouritesRoute);
    disableFilter(false);
    // Reset unsummarized markets modal event
    updateUnsummarizedMarketsModalEvent(null);

    const routeHandlerProps = {
      navigationTree,
      updateFiltering,
      setNavigationNodeIds: props.setNavigationNodeIds,
      updateCompetitionListType: props.updateCompetitionListType,
      updateCompetitionListTime: props.updateCompetitionListTime,
      setCompetitions: props.setCompetitions,
      setSingleCompetition: props.setSingleCompetition,
      disableFilter,
      updateUnsummarizedMarketsModalEvent,
      resetCompetitions: props.resetCompetitions,
      history,
      setSingleCompetitionOnly: props.setSingleCompetitionOnly,
    };
    if (path.startsWith("/competition/") && id) {
      handleCompetitionRoute(id, routeHandlerProps);
    } else if (path.startsWith("/sport/") && id) {
      handleSportRoute(id, filter, period, country, routeHandlerProps);
    } else if (path.startsWith("/events/")) {
      handleEventsRoute(routeHandlerProps, filter, period);
    } else if (path.startsWith("/bookmarks/")) {
      handleEventsRoute(routeHandlerProps);
    } else if (path.startsWith("/futures/")) {
      handleEventsRoute(routeHandlerProps);
    } else if (path.startsWith("/event/") && id) {
      handleEventRoute(id, routeHandlerProps);
    } else if (path.startsWith("/favourites/")) {
      handleFavouritesRoute(routeHandlerProps);
    } else {
      handleEventsRoute(routeHandlerProps, filter, period);
    }

    initialRouteHandled.current = true;
  }, [
    location.pathname,
    id,
    filter,
    period,
    country,
    navigationTree,
    isPageVisible,
  ]);

  // If filter is not set or is set to an invalid value, set popular as default
  useEffect(() => {
    const currentFilter = queryParams.get("filter");
    if (
      location.pathname.startsWith("/events/") ||
      location.pathname.startsWith("/sport/")
    ) {
      if (
        !currentFilter ||
        !Object.values(CompetitionFilterTypes).includes(
          currentFilter as CompetitionFilterType
        )
      ) {
        queryParams.set("filter", CompetitionFilterTypes.POPULAR);
        queryParams.delete("period"); //period is not needed if filter is set to popular
        history.replace({
          pathname: location.pathname,
          search: queryParams.toString(),
        });
      }
    }
  }, [history, location.pathname, location.search, queryParams]);

  // Load full list after initial load of 30 comps is done
  useEffect(() => {
    if (!isUserLoaded.current && !isUserLoading) {
      isUserLoaded.current = true;
    } else if (
      !isCompetitionListLoading &&
      isUserLoaded.current &&
      !fullListLoaded
    ) {
      if (
        fullListLoaded ||
        singleCompetitionOnly ||
        !initialRouteHandled.current
      ) {
        return;
      }
      setCompetitions({ fullList: true });
    }
  }, [
    isCompetitionListLoading,
    isUserLoading,
    fullListLoaded,
    singleCompetitionOnly,
    setCompetitions,
  ]);

  // Handle changing from mobile to desktop and vice versa
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= ResponsiveScreenSizesInt.mobile);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Add effect to handle idle state changes
  useEffect(() => {
    if (props.isIdle === false && prevIsIdle === true) {
      props.setCompetitions();
    }
    setPrevIsIdle(props.isIdle);
  }, [prevIsIdle, props, props.isIdle]);

  const setCompetitionsCallback = useCallback(() => {
    setCompetitions();
  }, [setCompetitions]);

  // If page was not visible due to using other tab, window minimised etc and is now visible
  usePageVisibility({
    isPageVisible: props.isPageVisible,
    loadNavigation: props.loadNavigation,
  });

  // Handle polling of competitions
  useCompetitionsPolling({
    selectedCompetitionType: props.selectedCompetitionType,
    time: props.time,
    singleCompetitionOnly: props.singleCompetitionOnly,
    isIdle: props.isIdle,
    setCompetitions: setCompetitionsCallback,
  });

  return (
    <div>
      {isMobile && <CompetitionsScreenMobile history={history} />}
      {!isMobile && <CompetitionsScreen history={history} />}
    </div>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  setCompetitions: options => {
    dispatch.competitionListModel.setEuroCompetitionList(options);
  },
  setSingleCompetition: (competitionId: number) => {
    dispatch.competitionListModel.setSingleCompetitionList(competitionId);
  },
  setNavigationNodeIds: (nodeIds: number[]) => {
    dispatch.navigationModel.setNavigationNodeIds(nodeIds);
  },
  updateCompetitionListType: (type: string) => {
    dispatch.competitionsListFiltersModel.updateCompetitionListType(type);
  },
  updateCompetitionListTime: (time: string) => {
    dispatch.competitionsListFiltersModel.updateCompetitionListTime(time);
  },
  updateFiltering: (
    navNodeIds: number[],
    selectedSport: NavigationMetaType,
    selectedCountry: NavigationMetaType,
    selectedCompetition: NavigationMetaType
  ) => {
    dispatch.navigationModel.updateFiltering({
      navNodeIds,
      selectedSport,
      selectedCountry,
      selectedCompetition,
    });
  },
  loadNavigation: () => dispatch.navigationModel.loadNavigation(),
  updateUnsummarizedMarketsModalEvent: (event: EventType | null) =>
    dispatch.competitionListModel.updateUnsummarizedMarketsModalEvent(event),
  resetCompetitions: () => {
    dispatch.competitionListModel.resetCompetition();
  },
  setBookmarksOnly: (bookmarksOnly: boolean) => {
    dispatch.competitionsListFiltersModel.setBookmarksOnly(bookmarksOnly);
  },
  setFutures: (futures: boolean) => {
    dispatch.competitionListModel.setFutures(futures);
  },
  setFavouritesOnly: (favouritesOnly: boolean) => {
    dispatch.competitionListModel.setFavouritesOnly(favouritesOnly);
  },
  disableFilter: (disableFilter: boolean) => {
    dispatch.competitionListModel.disableFilter(disableFilter);
  },
  setSingleCompetitionOnly: (singleCompetitionOnly: boolean) => {
    dispatch.competitionListModel.setSingleCompetitionOnly(
      singleCompetitionOnly
    );
  },
});
const mapStateToProps = state => ({
  isCompetitionListLoading: state.competitionListModel.isCompetitionListLoading,
  isUserLoading: state.userModel.isUserLoading,
  singleCompetitionOnly: state.competitionListModel.singleCompetitionOnly,
  fullListLoaded: state.competitionListModel.fullListLoaded,
  navigationTree: state.navigationModel.navigationTree,
  unsummarizedMarketsModalEvent:
    state.competitionListModel.unsummarizedMarketsModalEvent,
  selectedCompetitionType: state.competitionsListFiltersModel.type,
  isIdle: state.sessionModel.isIdle,
  time: state.competitionsListFiltersModel.time,
  isPageVisible: state.sessionModel.isPageVisible,
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CompetitionsScreenWrapper);
